/**
 * Roles Page - Microsoft 365 Style
 * Manage roles and assign to departments
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Badge,
} from '@fluentui/react-components';
import { EditRegular, DeleteRegular, PersonRegular, SearchRegular } from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../components/layout';
import { SearchableDropdown, TablePagination, SlidePanel } from '../../components/common';
import { usePagination } from '../../hooks/usePagination';
import { ApiClient, newToast } from '../../configurations/index.config';
import { useAppContext } from '../../contexts/AppContainer';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface Role {
  id: string;
  name: string;
  description?: string;
  department_id?: string;
  department_name?: string;
}

interface Department {
  id: string;
  department_name: string;
}

export const RoleFluentUI: React.FC = () => {
  const styles = useStyles();
  const { profileData } = useAppContext() as any;
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    departmentId: '',
  });

  useEffect(() => {
    if (profileData?.userId) {
      fetchRoles();
      fetchDepartments();
    }
  }, [profileData?.userId]);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-roles');
      if (response.data.status === 'success') {
        setRoles(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch roles');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchDepartments = async () => {
    try {
      const response = await ApiClient.get('/get-departments');
      if (response.data.status === 'success') {
        setDepartments(response.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setSelectedRole(role);
      setFormData({
        name: role.name,
        description: role.description || '',
        departmentId: role.department_id || '',
      });
    } else {
      setSelectedRole(null);
      setFormData({ name: '', description: '', departmentId: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      newToast('warning', 'Please enter role name');
      return;
    }

    try {
      // Using documented API endpoint with correct field names
      const payload = {
        name: formData.name,
        description: formData.description,
        department_id: formData.departmentId || null,
        ...(selectedRole && { id: selectedRole.id }),
      };

      const response = await ApiClient.post('/add-update-role', payload);

      if (response.data.status === 'success') {
        newToast(
          'success',
          selectedRole ? 'Role updated successfully' : 'Role created successfully'
        );
        fetchRoles();
        setDialogOpen(false);
      } else {
        newToast('error', response.data?.message || 'Failed to save role');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-role', {
        id: selectedRole.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Role deleted successfully');
        fetchRoles();
      } else {
        newToast('error', response.data?.message || 'Failed to delete role');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedRole(null);
  };

  const filteredRoles = roles.filter(role => {
    const query = searchQuery.toLowerCase();
    return (
      role.name?.toLowerCase().includes(query) ||
      role.description?.toLowerCase().includes(query) ||
      role.department_name?.toLowerCase().includes(query)
    );
  });

  // Pagination
  const { currentPage, itemsPerPage, paginatedData, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination({
      data: filteredRoles,
      initialPage: 1,
      initialItemsPerPage: 10,
    });

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Roles</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${roles.length} roles`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Role" />

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          placeholder="Search roles..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Roles Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading roles...</Text>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className={styles.emptyState}>
            <PersonRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No roles found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery
                ? 'Try adjusting your search'
                : 'Click "Add Role" to create your first role'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Role Name</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th}>Department</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map(role => (
                <tr key={role.id} className={styles.tr}>
                  <td className={styles.td}>
                    <Text weight="semibold">{role.name}</Text>
                  </td>
                  <td className={styles.td}>{role.description || '—'}</td>
                  <td className={styles.td}>
                    {role.department_name ? (
                      <Badge appearance="tint" color="brand">
                        {role.department_name}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EditRegular />}
                        onClick={() => handleOpenDialog(role)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => {
                          setSelectedRole(role);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filteredRoles.length > 0 && (
          <TablePagination
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        )}
      </ContentCard>

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedRole ? 'Edit Role' : 'Add Role'}
        subtitle={selectedRole ? 'Update role details' : 'Create a new role'}
        width="33%"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {selectedRole ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formField}>
          <Label required>Role Name</Label>
          <Input
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter role name"
          />
        </div>

        <div className={styles.formField}>
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter description (optional)"
            rows={3}
          />
        </div>

        <div className={styles.formField}>
          <Label>Department</Label>
          <SearchableDropdown
            placeholder="Search department..."
            options={[
              { id: '', label: 'None' },
              ...departments.map(dept => ({
                id: dept.id,
                label: dept.department_name,
              })),
            ]}
            selectedValue={formData.departmentId}
            onSelect={value => setFormData({ ...formData, departmentId: value })}
          />
        </div>
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedRole?.name}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default RoleFluentUI;
